import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  GpBulkStage,
  GpReviewPhoto,
  GpReviewStatus,
  GpReviewSummary,
  GpSyncedCategory,
  GpTone,
} from '../../growth-gbp-photos/types';
import {
  GP_BULK_PHOTOS,
  GP_BULK_TIPS,
  GP_PROCESSING_STEPS,
  GP_SYNCED_CATEGORIES,
} from '../../growth-gbp-photos/mockData';
import {
  GP_BULK_STEPS,
  GP_REVIEW_CATEGORIES,
  GP_REVIEW_FILTERS,
  GROWTH_GBP_PHOTOS_BULK_META,
} from '../../growth-gbp-photos/config';
import { Icon } from '../Icon/Icon';
import { Spinner } from '../Spinner/Spinner';
import './growth-gbp-photos.css';

const TONE: Record<GpTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const STATUS_META: Record<GpReviewStatus, { tone: GpTone; label: string }> = {
  ready: { tone: 'success', label: 'Ready' },
  warning: { tone: 'gold', label: 'Review needed' },
  uncategorized: { tone: 'info', label: 'Assign category' },
  rejected: { tone: 'terra', label: "Can't upload" },
};

function GpCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

const STAGE_INDEX: Record<GpBulkStage, number> = {
  upload: 0,
  processing: 1,
  review: 2,
  success: 3,
};

function GpStepper({ stage }: { stage: GpBulkStage }) {
  const current = STAGE_INDEX[stage];
  return (
    <GpCard className="mb-6 px-5 py-4">
      <div className="flex items-center gap-3">
        {GP_BULK_STEPS.map((step, index) => {
          const done = index < current;
          const active = index === current;
          return (
            <React.Fragment key={step.id}>
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-bold"
                  style={
                    done
                      ? { background: 'var(--success)', color: 'var(--cream-1)' }
                      : active
                        ? {
                            background: 'var(--success-tint)',
                            color: 'var(--success)',
                            border: '2px solid var(--success)',
                          }
                        : { background: 'var(--surface-mute)', color: 'var(--ink-3)' }
                  }
                >
                  {done ? <Icon kind="check" size={12} stroke={2.6} /> : index + 1}
                </span>
                <span
                  className="text-[13px] font-semibold"
                  style={{
                    color: active
                      ? 'var(--ink-0)'
                      : done
                        ? 'var(--success)'
                        : 'var(--ink-3)',
                  }}
                >
                  {step.label}
                </span>
              </div>
              {index < GP_BULK_STEPS.length - 1 ? (
                <div
                  className="h-px flex-1"
                  style={{ background: done ? 'var(--success)' : 'var(--cream-2)' }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </GpCard>
  );
}

function UploadStage({
  onFilesSelected,
  openFilePickerOnMount = false,
  uploading = false,
}: {
  onFilesSelected: (files: File[]) => void;
  openFilePickerOnMount?: boolean;
  uploading?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!openFilePickerOnMount || uploading) return;
    const timer = window.setTimeout(() => inputRef.current?.click(), 0);
    return () => window.clearTimeout(timer);
  }, [openFilePickerOnMount, uploading]);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
        className="hidden"
        disabled={uploading}
        onChange={(event) => {
          if (uploading) return;
          const picked = event.target.files;
          if (picked?.length) {
            onFilesSelected(Array.from(picked));
          }
          event.target.value = '';
        }}
      />
      <div
        className={[
          'mb-5 rounded-[var(--r-xl)] border-2 border-dashed px-10 py-14 text-center transition-opacity',
          uploading ? 'border-[var(--success)] bg-[var(--success-tint)] opacity-95' : 'border-[var(--cream-2)] bg-[var(--cream-1)]',
        ].join(' ')}
      >
        <span
          className={[
            'mx-auto mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-[var(--r-lg)]',
            uploading ? 'bg-[var(--cream-1)] text-[var(--success)]' : 'bg-[var(--success-tint)] text-[var(--success)]',
          ].join(' ')}
        >
          {uploading ? <Spinner size={28} color="var(--success)" /> : <Icon kind="upload" size={32} stroke={1.8} />}
        </span>
        <div className="font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          {uploading ? 'Uploading your photos…' : 'Drop photos here to upload'}
        </div>
        <p className="mx-auto mt-2 max-w-[480px] text-[14px] leading-[1.55] text-[var(--ink-3)]">
          {uploading
            ? 'Sending files to ELVA. This usually takes a few seconds — please keep this page open.'
            : "ELVA's AI will automatically categorize each photo, match it to our recommended prompts, and check image quality — all before anything goes to Google."}
        </p>
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-7 py-3 text-[14px] font-semibold text-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {uploading ? (
            <>
              <Spinner size={14} color="var(--cream-1)" />
              Uploading…
            </>
          ) : (
            <>
              Choose Files
              <Icon kind="arrow-r" size={14} stroke={2.2} />
            </>
          )}
        </button>
        <div className="mt-5 font-[var(--font-mono)] text-[12px] text-[var(--ink-4)]">
          JPG, PNG, HEIC accepted · Max 10 MB per photo · Up to 100 photos per upload
        </div>
      </div>

      <div
        className="gbp-bulk-tips-grid grid gap-4"
        style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
      >
        {GP_BULK_TIPS.map((tip) => (
          <GpCard key={tip.title} className="px-5 py-5">
            <span className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-[var(--r-md)] bg-[var(--surface-mute)] text-[var(--ai-3)]">
              <Icon kind={tip.icon} size={18} stroke={1.9} />
            </span>
            <div className="font-[var(--font-display)] text-[15px] font-semibold text-[var(--ink-0)]">
              {tip.title}
            </div>
            <p className="mt-1.5 text-[12.5px] leading-[1.55] text-[var(--ink-3)]">{tip.desc}</p>
          </GpCard>
        ))}
      </div>
    </>
  );
}

function ProcessingStage({
  total,
  processed,
  onCancel,
  polling,
}: {
  total: number;
  processed: number;
  onCancel: () => void;
  polling?: boolean;
}) {
  const safeTotal = total > 0 ? total : 1;
  const done = Math.min(processed, safeTotal);
  const pct = Math.round((done / safeTotal) * 100);
  const r = 34;
  const circ = 2 * Math.PI * r;

  return (
    <GpCard className="px-10 py-14 text-center">
      <div className="relative mx-auto mb-6 h-20 w-20">
        <svg width="80" height="80" className="-rotate-90">
          <circle cx="40" cy="40" r={r} fill="none" stroke="var(--surface-mute)" strokeWidth="6" />
          <circle
            cx="40"
            cy="40"
            r={r}
            fill="none"
            stroke="var(--success)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - pct / 100)}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-[var(--font-display)] text-[20px] font-bold text-[var(--ink-0)]">
          {pct}%
        </div>
      </div>

      <div className="font-[var(--font-display)] text-[20px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
        Analyzing your photos
      </div>
      <div className="mt-1.5 text-[13px] text-[var(--ink-3)]">
        Analyzed {done} of {safeTotal}
        {polling ? ' · Checking status…' : ''}
      </div>

      <div className="mx-auto mt-6 max-w-[420px] text-left">
        {GP_PROCESSING_STEPS.map((step) => (
          <div key={step.label} className="flex items-center gap-3 py-2 text-[13px]">
            <span
              className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full"
              style={
                step.done
                  ? { background: 'var(--success)' }
                  : step.active
                    ? { background: 'var(--success-tint)', border: '2px solid var(--success)' }
                    : { background: 'var(--surface-mute)' }
              }
            >
              {step.done ? <Icon kind="check" size={10} stroke={2.6} className="text-[var(--cream-1)]" /> : null}
              {step.active ? <span className="h-1.5 w-1.5 rounded-full bg-[var(--success)]" /> : null}
            </span>
            <span
              style={{
                color: step.done ? 'var(--success)' : step.active ? 'var(--ink-0)' : 'var(--ink-4)',
                fontWeight: step.active ? 600 : 400,
              }}
            >
              {step.label}
            </span>
            {step.active ? (
              <span className="ml-auto text-[11px] font-medium text-[var(--success)]">
                in progress...
              </span>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-center">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)]"
        >
          Cancel
        </button>
      </div>
    </GpCard>
  );
}

function GpPhotoReviewCard({
  photo,
  selected,
  onToggle,
  onRemove,
}: {
  photo: GpReviewPhoto;
  selected: boolean;
  onToggle: () => void;
  onRemove?: () => void;
}) {
  const meta = STATUS_META[photo.status];
  const statusTone = TONE[meta.tone];
  const rejected = photo.status === 'rejected';

  return (
    <GpCard
      className={[
        'overflow-hidden !p-0',
        selected ? '!border-[color-mix(in_srgb,var(--success)_45%,transparent)]' : '',
        rejected ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-[var(--surface-mute)]">
        {photo.previewUrl ? (
          <img src={photo.previewUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <Icon kind="image" size={26} stroke={1.6} className="text-[var(--ink-4)]" />
        )}
        <span className="sr-only">{photo.example}</span>

        <button
          type="button"
          onClick={rejected ? undefined : onToggle}
          disabled={rejected}
          aria-label={selected ? 'Deselect photo' : 'Select photo'}
          className="absolute left-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-[var(--r-sm)] disabled:cursor-not-allowed"
          style={
            selected
              ? { background: 'var(--success)', border: 'none', cursor: 'pointer' }
              : {
                  background: 'var(--cream-1)',
                  border: '1.5px solid var(--cream-2)',
                  cursor: rejected ? 'not-allowed' : 'pointer',
                }
          }
        >
          {selected ? <Icon kind="check" size={14} stroke={2.6} className="text-[var(--cream-1)]" /> : null}
        </button>

        <span
          className="absolute right-2 top-2 rounded-[var(--r-sm)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em]"
          style={{ background: statusTone.tint, color: statusTone.accent }}
        >
          {meta.label}
        </span>

        <span className="absolute bottom-2 left-2 rounded-[var(--r-sm)] bg-[var(--ink-0)] px-2 py-0.5 font-[var(--font-mono)] text-[10px] text-[var(--cream-1)]">
          {photo.fileName} · {photo.size}
        </span>

        {photo.requiresConsent ? (
          <span
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-[var(--r-sm)] px-2 py-0.5 text-[10px] font-semibold text-[var(--cream-1)]"
            style={{ background: 'var(--gold)' }}
          >
            <Icon kind="alert" size={10} stroke={2.4} />
            Consent
          </span>
        ) : null}
      </div>

      <div className="px-4 py-3.5">
        <div className={`${eyebrowClass} mb-1.5`}>AI Category</div>
        <div className="flex items-center gap-2">
          <select
            value={photo.category ?? ''}
            className="flex-1 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1.5 text-[12.5px] text-[var(--ink-1)]"
          >
            {photo.category ? null : <option value="">Assign category...</option>}
            {GP_REVIEW_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {photo.category ? (
            <span
              className="font-[var(--font-mono)] text-[10.5px] font-semibold"
              style={{ color: photo.confidence > 85 ? 'var(--success)' : 'var(--gold)' }}
            >
              {photo.confidence}%
            </span>
          ) : null}
        </div>

        {photo.matchedPrompt ? (
          <div
            className="mt-2.5 flex items-center gap-1.5 rounded-[var(--r-sm)] px-3 py-2"
            style={{ background: 'var(--success-tint)' }}
          >
            <Icon kind="check-circ" size={12} stroke={2.2} className="text-[var(--success)]" />
            <span className="text-[11.5px] text-[var(--ink-2)]">
              Matches prompt: <strong className="font-semibold">{photo.matchedPrompt}</strong>
            </span>
          </div>
        ) : null}

        {photo.additional ? (
          <div className="mt-2.5 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-2.5 py-1.5 text-[11px] text-[var(--ink-3)]">
            No prompt match — will be saved as additional photo in category
          </div>
        ) : null}

        {photo.qualityIssue ? (
          <div
            className="mt-2.5 flex gap-2 rounded-[var(--r-sm)] px-3 py-2"
            style={{ background: rejected ? TONE.terra.tint : TONE.gold.tint }}
          >
            <Icon
              kind="alert"
              size={13}
              stroke={2.2}
              className="mt-0.5 shrink-0"
              style={{ color: rejected ? 'var(--terracotta)' : 'var(--gold)' }}
            />
            <span
              className="text-[11.5px] leading-[1.5]"
              style={{ color: rejected ? 'var(--terracotta)' : 'var(--gold)' }}
            >
              {photo.qualityIssue}
            </span>
          </div>
        ) : null}

        <div className={`${eyebrowClass} mb-1.5 mt-3`}>Description (optional)</div>
        <input
          type="text"
          defaultValue={photo.description}
          placeholder="Add a description..."
          className="w-full rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1.5 text-[12.5px] text-[var(--ink-1)]"
        />

        <div className="mt-3 flex gap-1.5">
          {rejected ? (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex w-full cursor-pointer items-center justify-center rounded-[var(--r-sm)] border px-3 py-1.5 text-[11.5px] font-medium"
              style={{
                borderColor: 'color-mix(in srgb, var(--terracotta) 35%, transparent)',
                background: 'var(--cream-1)',
                color: 'var(--terracotta)',
              }}
            >
              Remove from batch
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onRemove}
                className="inline-flex flex-1 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[11.5px] font-medium text-[var(--ink-2)]"
              >
                Remove
              </button>
              <button
                type="button"
                onClick={() => {
                  if (photo.previewUrl) window.open(photo.previewUrl, '_blank', 'noopener,noreferrer');
                }}
                disabled={!photo.previewUrl}
                className="inline-flex flex-1 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[11.5px] font-medium text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                View full
              </button>
            </>
          )}
        </div>
      </div>
    </GpCard>
  );
}

function ReviewStage({
  photos,
  summary,
  onSync,
  onCancel,
  onRemoveSlot,
  syncing,
}: {
  photos: GpReviewPhoto[];
  summary?: GpReviewSummary;
  onSync: (slotIds: number[]) => void | Promise<void>;
  onCancel: () => void;
  onRemoveSlot?: (slotId: number) => void;
  syncing?: boolean;
}) {
  const [filter, setFilter] = useState<GpReviewStatus | 'all'>('all');
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(photos.filter((p) => p.status === 'ready').map((p) => p.id)),
  );

  const counts = useMemo(() => {
    const base: Record<GpReviewStatus | 'all', number> = {
      all: photos.length,
      ready: 0,
      warning: 0,
      uncategorized: 0,
      rejected: 0,
    };
    photos.forEach((p) => {
      base[p.status] += 1;
    });
    return base;
  }, [photos]);

  const filtered = filter === 'all' ? photos : photos.filter((p) => p.status === filter);

  const summaryStats: { label: string; value: number; tone: GpTone }[] = [
    { label: 'Uploaded', value: summary?.uploaded ?? counts.all, tone: 'neutral' },
    { label: 'Auto-ready', value: summary?.autoReady ?? counts.ready, tone: 'success' },
    { label: 'Need review', value: summary?.needReview ?? counts.warning, tone: 'gold' },
    { label: 'Uncategorized', value: summary?.uncategorized ?? counts.uncategorized, tone: 'info' },
    { label: 'Rejected', value: summary?.rejected ?? counts.rejected, tone: 'terra' },
  ];

  return (
    <>
      <div
        className="gbp-bulk-summary-grid mb-5 grid gap-2.5"
        style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
      >
        {summaryStats.map((stat) => (
          <GpCard key={stat.label} className="px-4 py-3.5">
            <div className={eyebrowClass}>{stat.label}</div>
            <div
              className="mt-1.5 font-[var(--font-display)] text-[22px] font-bold leading-none tracking-[-0.03em]"
              style={{ color: stat.tone === 'neutral' ? 'var(--ink-0)' : TONE[stat.tone].accent }}
            >
              {stat.value}
            </div>
          </GpCard>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--cream-2)] pb-3">
        <div className="flex flex-wrap gap-1">
          {GP_REVIEW_FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => {
                  setFilter(f.id);
                }}
                className="inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent px-3 py-2 text-[13px] font-medium"
                style={{
                  color: active ? 'var(--success)' : 'var(--ink-3)',
                  borderBottom: active ? '2px solid var(--success)' : '2px solid transparent',
                  marginBottom: -13,
                }}
              >
                {f.label}
                <span
                  className="inline-flex items-center rounded-[var(--r-pill)] px-1.5 py-0.5 text-[11px] font-semibold"
                  style={{
                    background: active ? 'var(--success-tint)' : 'var(--surface-mute)',
                    color: active ? 'var(--success)' : 'var(--ink-3)',
                  }}
                >
                  {counts[f.id]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] text-[var(--ink-3)]">{selected.size} selected</span>
          <button
            type="button"
            className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12px] font-medium text-[var(--ink-2)]"
            onClick={() => {
              setSelected(new Set(photos.filter((p) => p.status === 'ready').map((p) => p.id)));
            }}
          >
            Select all ready
          </button>
        </div>
      </div>

      <div
        className="gbp-bulk-review-grid grid gap-3.5"
        style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
      >
        {filtered.map((photo) => (
          <GpPhotoReviewCard
            key={photo.id}
            photo={photo}
            selected={selected.has(photo.id)}
            onToggle={() => {
              setSelected((current) => {
                const next = new Set(current);
                if (next.has(photo.id)) next.delete(photo.id);
                else next.add(photo.id);
                return next;
              });
            }}
            {...(onRemoveSlot ? { onRemove: () => onRemoveSlot(photo.patchId) } : {})}
          />
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-4 shadow-[var(--sh-1)]">
        <div className="text-[13px] text-[var(--ink-3)]">
          <strong className="font-semibold text-[var(--ink-0)]">{selected.size} photos</strong> ready
          to sync · They&apos;ll appear on your GBP within 5 minutes
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2.5 text-[13px] font-medium text-[var(--ink-2)]"
          >
            Cancel Upload
          </button>
          <button
            type="button"
            onClick={() => {
              const slotIds = photos
                .filter((photo) => selected.has(photo.id))
                .map((photo) => photo.patchId);
              void Promise.resolve(onSync(slotIds));
            }}
            disabled={selected.size === 0 || syncing}
            className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-5 py-2.5 text-[14px] font-semibold text-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {syncing ? 'Syncing…' : `Sync ${selected.size} to Google`}
            <Icon kind="arrow-r" size={14} stroke={2.2} />
          </button>
        </div>
      </div>
    </>
  );
}

function SuccessStage({
  syncedCategories,
  onReturn,
  onUploadMore,
}: {
  syncedCategories?: GpSyncedCategory[];
  onReturn: () => void;
  onUploadMore: () => void;
}) {
  const categories = syncedCategories?.length ? syncedCategories : GP_SYNCED_CATEGORIES;
  const synced = categories.reduce((sum, c) => sum + c.count, 0);
  return (
    <GpCard className="mx-auto max-w-[620px] px-10 py-14 text-center">
      <span className="mx-auto mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[var(--success-tint)] text-[var(--success)]">
        <Icon kind="check" size={34} stroke={2.4} />
      </span>
      <div className="font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">
        {synced} photos synced to Google
      </div>
      <p className="mx-auto mt-2 max-w-[460px] text-[14px] leading-[1.6] text-[var(--ink-3)]">
        Your photos will appear on your Google Business Profile within 5 minutes. Google typically
        takes up to 24 hours to fully index new images.
      </p>

      <div className="mt-6 rounded-[var(--r-md)] bg-[var(--surface-mute)] px-6 py-5 text-left">
        <div className={`${eyebrowClass} mb-3`}>What was uploaded</div>
        <div className="flex flex-col gap-1.5">
          {categories.map((cat) => (
            <div key={cat.label} className="flex items-center justify-between py-1 text-[13px]">
              <span className="text-[var(--ink-2)]">{cat.label}</span>
              <span className="font-[var(--font-mono)] font-semibold text-[var(--ink-0)]">
                {cat.count} photo{cat.count !== 1 ? 's' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex justify-center gap-2.5">
        <button
          type="button"
          onClick={onUploadMore}
          className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-2.5 text-[14px] font-medium text-[var(--ink-2)]"
        >
          Upload More Photos
        </button>
        <button
          type="button"
          onClick={onReturn}
          className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-5 py-2.5 text-[14px] font-semibold text-[var(--cream-1)]"
        >
          Return to GBP Photos
          <Icon kind="arrow-r" size={14} stroke={2.2} />
        </button>
      </div>
    </GpCard>
  );
}

export interface GbpPhotosBulkUploadViewProps {
  loading?: boolean;
  openFilePickerOnMount?: boolean;
  processingProgress?: { total: number; processed: number };
  processingComplete?: boolean;
  polling?: boolean;
  reviewPhotos?: GpReviewPhoto[];
  reviewSummary?: GpReviewSummary;
  syncedCategories?: GpSyncedCategory[];
  onUpload?: (files: File[]) => void | Promise<void>;
  onSync?: (slotIds: number[]) => void | Promise<void>;
  onRemoveSlot?: (slotId: number) => void;
  onCancelSession?: () => void;
  onReturn?: () => void;
  syncing?: boolean;
}

export function GbpPhotosBulkUploadView({
  loading = false,
  openFilePickerOnMount = false,
  processingProgress,
  processingComplete = false,
  polling = false,
  reviewPhotos,
  reviewSummary,
  syncedCategories,
  onUpload,
  onSync,
  onRemoveSlot,
  onCancelSession,
  onReturn,
  syncing = false,
}: GbpPhotosBulkUploadViewProps = {}) {
  const meta = GROWTH_GBP_PHOTOS_BULK_META;
  const [stage, setStage] = useState<GpBulkStage>('upload');
  const [localPreviewUrls, setLocalPreviewUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const isUploading = uploading || loading;

  const reviewList = useMemo((): GpReviewPhoto[] => {
    const photos = reviewPhotos ?? GP_BULK_PHOTOS;
    if (!reviewPhotos?.length || localPreviewUrls.length === 0) return photos;
    return photos.map((photo, index) => {
      const fallbackPreview = localPreviewUrls[index];
      if (photo.previewUrl || !fallbackPreview) return photo;
      return { ...photo, previewUrl: fallbackPreview };
    });
  }, [reviewPhotos, localPreviewUrls]);

  useEffect(() => {
    return () => {
      localPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [localPreviewUrls]);

  useEffect(() => {
    if (stage === 'processing' && processingComplete && reviewList.length > 0) {
      setStage('review');
    }
  }, [stage, processingComplete, reviewList.length]);

  const handleFilesSelected = (files: File[]) => {
    if (files.length === 0 || isUploading) return;
    setLocalPreviewUrls((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return files.map((file) => URL.createObjectURL(file));
    });
    if (!onUpload) {
      setStage('processing');
      return;
    }
    setUploading(true);
    void Promise.resolve(onUpload(files))
      .then(() => {
        setStage('processing');
      })
      .catch(() => undefined)
      .finally(() => {
        setUploading(false);
      });
  };

  const subtitle =
    isUploading && stage === 'upload'
      ? 'Uploading your photos — please wait.'
      : stage === 'processing'
      ? 'ELVA is analyzing your photos using AI. This usually takes 5-30 seconds.'
      : stage === 'review'
        ? "Review ELVA's analysis for each photo. Fix categories, check quality flags, then sync to Google."
        : meta.description;

  return (
    <div className="growth-gbp-photos-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="upload" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {stage === 'success' ? (
            <>
              Upload <span className="elva-spectrum">complete</span>
            </>
          ) : (
            <>
              {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
            </>
          )}
        </h1>
        {stage === 'success' ? null : (
          <p className="mt-2.5 max-w-[640px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {subtitle}
          </p>
        )}
      </div>

      {stage === 'success' ? null : <GpStepper stage={stage} />}

      {stage === 'upload' ? (
        <UploadStage
          openFilePickerOnMount={openFilePickerOnMount}
          uploading={isUploading}
          onFilesSelected={handleFilesSelected}
        />
      ) : null}
      {stage === 'processing' ? (
        <ProcessingStage
          total={processingProgress?.total ?? GP_BULK_PHOTOS.length}
          processed={processingProgress?.processed ?? 0}
          polling={polling}
          onCancel={() => {
            onCancelSession?.();
            setStage('upload');
          }}
        />
      ) : null}
      {stage === 'review' ? (
        <ReviewStage
          photos={reviewList}
          {...(reviewSummary ? { summary: reviewSummary } : {})}
          onSync={(slotIds) => {
            if (!onSync) {
              setStage('success');
              return;
            }
            void Promise.resolve(onSync(slotIds)).then(() => {
              setStage('success');
            });
          }}
          onCancel={() => {
            onCancelSession?.();
            setStage('upload');
          }}
          {...(onRemoveSlot ? { onRemoveSlot } : {})}
          syncing={syncing}
        />
      ) : null}
      {stage === 'success' ? (
        <SuccessStage
          {...(syncedCategories?.length ? { syncedCategories } : {})}
          onReturn={() => {
            if (onReturn) {
              onReturn();
              return;
            }
            setStage('upload');
          }}
          onUploadMore={() => {
            setStage('upload');
          }}
        />
      ) : null}
    </div>
  );
}
